import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart3, Info, Clock, CheckCircle2, IndianRupee, UserX, AlarmClock, CalendarDays } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ReportMetricTile from '../components/reports/ReportMetricTile';
import StaffScorecardTable from '../components/reports/StaffScorecardTable';
import MusterExportButton from '../components/reports/MusterExportButton';
import apiClient from '../api/axios';
import { formatCurrency } from '../utils/format';
import { downloadMusterCsv } from '../utils/downloadMusterCsv';

// Backend returns every active employee unbounded (no paging on /api/reports/leave-summary or
// /api/reports/staff). This mirrors the 100-employee cap used elsewhere in the app (e.g.
// Team.jsx) so both tables have an explicit bound before restaurants at that scale exist,
// rather than relying on "today it happens to be small."
const MAX_ROWS = 100;

function pad(n) {
  return String(n).padStart(2, '0');
}

function isValidMonthKey(value) {
  return typeof value === 'string' && /^\d{4}-\d{2}$/.test(value);
}

function formatDecimal(value, language) {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 1 }).format(value);
}

function formatCount(value, language) {
  return new Intl.NumberFormat(language).format(Math.round(value));
}

function Reports() {
  const { t, i18n } = useTranslation('reports');

  const now = new Date();
  const [summaryMonth, setSummaryMonth] = useState(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(null);

  const fetchSummary = useCallback(async (monthKey) => {
    if (!isValidMonthKey(monthKey)) return;
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const res = await apiClient.get('/api/reports/summary', {
        params: { monthStart: `${monthKey}-01` },
      });
      setSummaryData(res.data);
    } catch (err) {
      setSummaryError(err.response?.data?.message || t('summary.errors.loadFailed'));
    } finally {
      setSummaryLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSummary(summaryMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryMonth]);

  // Story 21-B — staff scorecard, driven by the same month picker as the 21-A tiles above.
  const [staffRows, setStaffRows] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffError, setStaffError] = useState(null);

  const fetchStaff = useCallback(async (monthKey) => {
    if (!isValidMonthKey(monthKey)) return;
    setStaffLoading(true);
    setStaffError(null);
    try {
      const res = await apiClient.get('/api/reports/staff', {
        params: { monthStart: `${monthKey}-01` },
      });
      // Mirrors the leave-summary table's existing unbounded-backend/capped-frontend pattern
      // (MAX_ROWS) rather than a backend reject — a restaurant's staff count is inherently bounded.
      setStaffRows((res.data?.rows ?? []).slice(0, MAX_ROWS));
    } catch (err) {
      setStaffError(err.response?.data?.message || t('staffScorecard.errors.loadFailed'));
    } finally {
      setStaffLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStaff(summaryMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryMonth]);

  let monthLabel = '';
  if (isValidMonthKey(summaryMonth)) {
    const [summaryYear, summaryMonthNum] = summaryMonth.split('-').map(Number);
    monthLabel = new Intl.DateTimeFormat(i18n.language, { month: 'long', year: 'numeric' })
      .format(new Date(summaryYear, summaryMonthNum - 1, 1));
  }

  const headlineText = summaryData && monthLabel
    ? t('summary.headline', {
        month: monthLabel,
        scheduledHours: formatDecimal(summaryData.scheduledHours.thisMonth, i18n.language),
        labourCost: formatCurrency(summaryData.labourCostPaise.thisMonth / 100, { decimals: 2 }),
        noShows: formatCount(summaryData.noShows.thisMonth, i18n.language),
        leaveDays: formatCount(summaryData.approvedLeaveDays.thisMonth, i18n.language),
      })
    : '';

  // Story 21-POLISH — the leave summary is now an annual roll-up (no separate period toggle),
  // driven by the *year* of the master month picker (summaryMonth) so it can never fall out of
  // sync with the tiles/scorecard above it. The effect depends on `leaveYear`, not `summaryMonth`,
  // so picking a different month within the same year does not re-query it.
  const leaveYear = isValidMonthKey(summaryMonth) ? summaryMonth.split('-')[0] : null;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async (year) => {
    if (!year) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/api/reports/leave-summary', {
        params: { startDate: `${year}-01-01`, endDate: `${year}-12-31` },
      });
      setRows((res.data ?? []).slice(0, MAX_ROWS));
    } catch (err) {
      setError(err.response?.data?.message || t('leaveSummary.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchReport(leaveYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveYear]);

  // Story 25 — payroll muster CSV export, independent of the schedule CSV export on the
  // Scheduler page. No period state to track: each pick computes its own from/to and fires
  // the download immediately.
  const [musterExporting, setMusterExporting] = useState(false);
  const [musterError, setMusterError] = useState(null);

  const handleMusterExport = async (from, to) => {
    setMusterExporting(true);
    setMusterError(null);
    try {
      await downloadMusterCsv(from, to);
    } catch (err) {
      setMusterError(err.response?.data?.message || t('musterExport.failed'));
    } finally {
      setMusterExporting(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <PageHeader title={t('title')} actions={[
        <MusterExportButton key="export-muster" onExport={handleMusterExport} exporting={musterExporting} />,
      ]} />

      <div className="px-6 py-6 space-y-6">
        {musterError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {musterError}
          </div>
        )}

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm font-medium text-gray-700">
              {summaryLoading ? ' ' : headlineText}
            </p>
            <label className="flex items-center gap-2 text-sm text-gray-600 self-start">
              {t('summary.monthPickerLabel')}
              <input
                type="month"
                value={summaryMonth}
                onChange={e => setSummaryMonth(e.target.value)}
                className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-500"
              />
            </label>
          </div>

          {summaryError && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {summaryError}
            </div>
          )}

          {!summaryError && (
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              {summaryLoading || !summaryData ? (
                [...Array(6)].map((_, i) => (
                  <div key={i} className="rounded-xl bg-white border border-gray-200 shadow-sm p-4 h-24">
                    <div className="h-3 rounded bg-gray-100 animate-pulse" />
                  </div>
                ))
              ) : (
                <>
                  <ReportMetricTile
                    icon={<Clock size={15} />}
                    label={t('summary.tiles.scheduledHours')}
                    valueDisplay={`${formatDecimal(summaryData.scheduledHours.thisMonth, i18n.language)} ${t('summary.hoursUnit')}`}
                    metric={summaryData.scheduledHours}
                    coloring="neutral"
                  />
                  <ReportMetricTile
                    icon={<CheckCircle2 size={15} />}
                    label={t('summary.tiles.actualHours')}
                    valueDisplay={`${formatDecimal(summaryData.actualHours.thisMonth, i18n.language)} ${t('summary.hoursUnit')}`}
                    metric={summaryData.actualHours}
                    coloring="neutral"
                  />
                  <ReportMetricTile
                    icon={<IndianRupee size={15} />}
                    label={t('summary.tiles.labourCost')}
                    valueDisplay={formatCurrency(summaryData.labourCostPaise.thisMonth / 100, { decimals: 2 })}
                    metric={summaryData.labourCostPaise}
                    coloring="neutral"
                  />
                  <ReportMetricTile
                    icon={<UserX size={15} />}
                    label={t('summary.tiles.noShows')}
                    valueDisplay={formatCount(summaryData.noShows.thisMonth, i18n.language)}
                    metric={summaryData.noShows}
                    coloring="inverse"
                  />
                  <ReportMetricTile
                    icon={<AlarmClock size={15} />}
                    label={t('summary.tiles.lateArrivals')}
                    valueDisplay={formatCount(summaryData.lateArrivals.thisMonth, i18n.language)}
                    metric={summaryData.lateArrivals}
                    coloring="inverse"
                  />
                  <ReportMetricTile
                    icon={<CalendarDays size={15} />}
                    label={t('summary.tiles.leaveDays')}
                    valueDisplay={formatCount(summaryData.approvedLeaveDays.thisMonth, i18n.language)}
                    metric={summaryData.approvedLeaveDays}
                    coloring="neutral"
                  />
                </>
              )}
            </div>
          )}
        </div>

        <StaffScorecardTable rows={staffRows} loading={staffLoading} error={staffError} />

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 pt-4">
            <h2 className="text-sm font-semibold text-gray-800">
              {t('leaveSummary.heading', { year: leaveYear })}
            </h2>
          </div>

          {error && (
            <div className="mx-4 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <BarChart3 size={36} className="text-gray-300 mb-3" />
              <p className="text-sm font-medium text-gray-600">{t('leaveSummary.empty')}</p>
            </div>
          )}

          {(loading || rows.length > 0) && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leaveSummary.columns.staff')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leaveSummary.columns.approvedDays')}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('leaveSummary.columns.pending')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading && [...Array(3)].map((_, i) => (
                    <tr key={i}>
                      <td colSpan={3} className="px-4 py-3">
                        <div className="h-3 rounded bg-gray-100 animate-pulse" />
                      </td>
                    </tr>
                  ))}
                  {!loading && rows.map(row => (
                    <tr key={row.employeeId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.employeeName || '—'}</td>
                      <td className="px-4 py-3 text-gray-700">{row.approvedDays}</td>
                      <td className="px-4 py-3 text-gray-700">{row.pendingCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-start gap-2 px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            <Info size={13} className="mt-0.5 shrink-0" />
            <span>{t('leaveSummary.activeOnlyNote')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Reports;
